import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { View, Text, Animated, LayoutChangeEvent } from 'react-native';
import { DbProgressProps } from './DbProgress.types';
import { styles, defaultColors } from './DbProgress.styles';

// 最小高度阈值，低于此高度时 follow 模式会自动切换为 right 模式
const MIN_HEIGHT_FOR_FOLLOW = 20;

/**
 * DbProgress 进度条组件
 * 
 * 用于展示任务进度、完成率等场景
 * 
 * @example
 * // 基础用法
 * <DbProgress value={50} />
 * 
 * @example
 * // 显示百分比
 * <DbProgress value={50} showPercent percentPosition="follow" />
 * 
 * @example
 * // 自定义颜色和高度
 * <DbProgress value={70} color="#4CAF50" height={12} />
 * 
 * @example
 * // 自定义指示器
 * <DbProgress 
 *   value={60} 
 *   renderIndicator={() => <Image source={require('./icon.png')} />} 
 * />
 */
export const DbProgress: React.FC<DbProgressProps> = ({
    value,
    color = defaultColors.progress,
    trackColor = defaultColors.track,
    height = 8,
    borderRadius,
    showPercent = false,
    percentPosition = 'follow',
    percentColor,
    percentFontSize = 12,
    renderIndicator,
    animated = true,
    animationDuration = 300,
    style,
    progressStyle,
    trackStyle,
    percentStyle,
}) => {
    // 确保值在 0-100 之间
    const clampedValue = Math.max(0, Math.min(100, value));
    
    // 当高度小于阈值时，follow 模式自动切换为 right 模式
    const effectivePercentPosition = useMemo(() => {
        if (percentPosition === 'follow' && height < MIN_HEIGHT_FOR_FOLLOW) {
            return 'right';
        }
        return percentPosition;
    }, [percentPosition, height]);
    
    // 动画值
    const animatedWidth = useRef(new Animated.Value(0)).current;
    
    // 轨道宽度
    const [trackWidth, setTrackWidth] = useState(0);
    
    // 计算圆角
    const computedBorderRadius = useMemo(() => {
        return borderRadius !== undefined ? borderRadius : height / 2;
    }, [borderRadius, height]);
    
    // 计算百分比文字颜色
    const computedPercentColor = useMemo(() => {
        if (percentColor) return percentColor;
        // 当自动切换到 right 模式时，使用深色文字
        if (effectivePercentPosition === 'right' && percentPosition === 'follow') {
            return color; // 使用进度条颜色
        }
        if (effectivePercentPosition === 'inside' || effectivePercentPosition === 'follow') {
            return defaultColors.percentInside;
        }
        return defaultColors.percentOutside;
    }, [percentColor, effectivePercentPosition, percentPosition, color]);
    
    // 处理轨道布局
    const handleTrackLayout = useCallback((event: LayoutChangeEvent) => {
        const { width } = event.nativeEvent.layout;
        setTrackWidth(width);
    }, []);
    
    // 动画效果
    useEffect(() => {
        if (animated) {
            Animated.timing(animatedWidth, {
                toValue: clampedValue,
                duration: animationDuration,
                useNativeDriver: false,
            }).start();
        } else {
            animatedWidth.setValue(clampedValue);
        }
    }, [clampedValue, animated, animationDuration, animatedWidth]);
    
    // 计算进度条宽度百分比
    const progressWidthStyle = useMemo(() => {
        if (animated) {
            return {
                width: animatedWidth.interpolate({
                    inputRange: [0, 100],
                    outputRange: ['0%', '100%'],
                }),
            };
        }
        return { width: `${clampedValue}%` as const };
    }, [animated, animatedWidth, clampedValue]);
    
    // 计算指示器位置
    const indicatorPosition = useMemo(() => {
        if (!trackWidth) return 0;
        return (trackWidth * clampedValue) / 100;
    }, [trackWidth, clampedValue]);
    
    // 渲染百分比文本
    const renderPercentText = () => {
        if (!showPercent) return null;
        
        const percentText = `${Math.round(clampedValue)}%`;
        
        if (effectivePercentPosition === 'right') {
            return (
                <Text 
                    style={[
                        styles.percentText,
                        styles.percentRight,
                        { fontSize: percentFontSize, color: computedPercentColor },
                        percentStyle
                    ]}
                >
                    {percentText}
                </Text>
            );
        }
        
        if (effectivePercentPosition === 'follow') {
            // 跟随进度条位置（仅当高度足够大时使用）
            return (
                <View 
                    style={[
                        styles.percentFollow,
                        { left: Math.max(indicatorPosition - 20, 0) }
                    ]}
                >
                    <Text 
                        style={[
                            styles.percentText,
                            { fontSize: percentFontSize, color: computedPercentColor },
                            percentStyle
                        ]}
                    >
                        {percentText}
                    </Text>
                </View>
            );
        }
        
        // inside
        return (
            <View style={styles.percentInside}>
                <Text 
                    style={[
                        styles.percentText,
                        { fontSize: percentFontSize, color: computedPercentColor },
                        percentStyle
                    ]}
                >
                    {percentText}
                </Text>
            </View>
        );
    };
    
    // 渲染自定义指示器
    const renderCustomIndicator = () => {
        if (!renderIndicator || !trackWidth) return null;
        
        return (
            <View 
                style={[
                    styles.indicatorContainer,
                    { 
                        left: indicatorPosition,
                        transform: [{ translateX: -12 }], // 居中调整
                    }
                ]}
            >
                {renderIndicator(clampedValue)}
            </View>
        );
    };

    return (
        <View style={[styles.container, style]}>
            <View style={styles.trackContainer}>
                <View 
                    style={[
                        styles.track, 
                        { 
                            height,
                            borderRadius: computedBorderRadius,
                            backgroundColor: trackColor,
                        },
                        trackStyle
                    ]}
                    onLayout={handleTrackLayout}
                >
                    <Animated.View 
                        style={[
                            styles.progress,
                            {
                                backgroundColor: color,
                                borderRadius: computedBorderRadius,
                            },
                            progressWidthStyle,
                            progressStyle
                        ]}
                    />
                    {effectivePercentPosition === 'inside' && renderPercentText()}
                    {effectivePercentPosition === 'follow' && renderPercentText()}
                    {renderCustomIndicator()}
                </View>
                {effectivePercentPosition === 'right' && renderPercentText()}
            </View>
        </View>
    );
};

// 保持向后兼容的别名
export const Progress = DbProgress;

export default DbProgress;
export type { DbProgressProps } from './DbProgress.types';
